import { bool, func, node, string } from "prop-types";
import { forwardRef } from "react";

import styled from "styled-components/macro";

import { AnimatePresence, motion } from "framer-motion";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

import useAnimationStyles from "./hooks";

const Component = forwardRef(
  (
    {
      show,
      bottom = false,
      top = !bottom, // This way, if bottom is set to true there is no need to set top to false.
      left = false,
      right = false,
      height,
      width,
      muted,
      setIsAnimating,
      children,
      ...rest
    },
    ref
  ) => {
    const isMounted = useComponentIsMounted();

    const { visible, hidden } = useAnimationStyles({
      top,
      bottom,
      left,
      right,
      height,
      width,
    });

    return (
      <AnimatePresence>
        {show && (
          <motion.div
            ref={ref}
            initial={show && !isMounted ? visible : hidden}
            animate={visible}
            exit={hidden}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
            {...rest}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
);

Component.propTypes = {
  show: bool,
  muted: bool,
  height: string,
  width: string,
  top: bool,
  bottom: bool,
  left: bool,
  right: bool,
  children: node,
  setIsAnimating: func,
};

const FnrFade = forwardRef(
  /**
   * A simple disappearing trick.
   * @param {object} param0
   * @param {boolean} param0.show - Should this be displaying?
   * @param {string} param0.height - default to auto; change as needed
   * @param {string} param0.width - default to auto; change as needed
   * @param {boolean} param0.muted - Deactivates effect
   * @param {boolean} param0.top - Causes wrapped component to come in from the top. Defaults to true
   * @param {boolean} param0.bottom - Causes wrapped component to come in from the bottom
   * @param {boolean} param0.left - Causes wrapped component to come in from the left
   * @param {boolean} param0.right - Causes wrapped component to come in from the right
   * @param {import("react").RefObject} ref
   */
  (
    {
      muted,
      show,
      height = "auto",
      width = "auto",
      setIsAnimating = () => {},
      ...rest
    },
    ref
  ) => {
    if (muted && !show) {
      return null;
    } else if (muted) {
      return (
        <SDiv
          ref={ref}
          $height={height}
          $width={width}
          data-cy="FnrFade_Muted"
          {...rest}
        />
      );
    }
    return (
      <Component
        ref={ref}
        height={height}
        width={width}
        show={show}
        data-cy="FnrFade"
        setIsAnimating={setIsAnimating}
        {...rest}
      />
    );
  }
);

const SDiv = styled("div")`
  ${({ $width }) => `width: ${$width};`}
  ${({ $height }) => `height: ${$height};`}
`;

FnrFade.propTypes = {
  show: bool,
  muted: bool,
  height: string,
  width: string,
  top: bool,
  bottom: bool,
  left: bool,
  right: bool,
  setIsAnimating: func,
};

export default FnrFade;
